import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFContributionCarryForward = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    
    // Get current financial year
    const getCurrentFinancialYear = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1; // 0-based
        // Financial year starts July 1
        return month >= 7 ? year : year - 1;
    };

    const currentFY = getCurrentFinancialYear();
    
    // Initialize 5 years of history (current + 4 previous)
    const [carryForwardData, setCarryForwardData] = useState({
        totalSuperBalance: 500000, // TSB at start of current FY
        currentYear: {
            year: currentFY,
            concessionalUsed: 15000,
            concessionalCap: FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL
        },
        previousYears: [
            { year: currentFY - 1, concessionalUsed: 20000, concessionalCap: 30000 },
            { year: currentFY - 2, concessionalUsed: 25000, concessionalCap: 30000 },
            { year: currentFY - 3, concessionalUsed: 10000, concessionalCap: 27500 },
            { year: currentFY - 4, concessionalUsed: 15000, concessionalCap: 27500 }
        ]
    });

    const [results, setResults] = useState(null);

    // Calculate carry-forward amounts
    const calculateCarryForward = () => {
        const tsbThreshold = FinancialConfig.TSB_THRESHOLDS.CARRY_FORWARD_CUTOFF;
        const isEligible = carryForwardData.totalSuperBalance < tsbThreshold;

        if (!isEligible) {
            setResults({
                eligible: false,
                tsbThreshold,
                currentTSB: carryForwardData.totalSuperBalance,
                reason: `Total Super Balance ($${carryForwardData.totalSuperBalance.toLocaleString()}) exceeds threshold ($${tsbThreshold.toLocaleString()})`
            });
            setActiveTab('results');
            return;
        }

        // Calculate unused amounts for each previous year
        const unusedAmounts = carryForwardData.previousYears.map(year => ({
            year: year.year,
            cap: year.concessionalCap,
            used: year.concessionalUsed,
            unused: Math.max(0, year.concessionalCap - year.concessionalUsed),
            expired: (currentFY - year.year) >= 5
        })).filter(y => !y.expired); // Only include years within 5-year window

        const totalUnused = unusedAmounts.reduce((sum, y) => sum + y.unused, 0);
        const currentYearCap = carryForwardData.currentYear.concessionalCap;
        const currentYearUsed = carryForwardData.currentYear.concessionalUsed;
        const currentYearRemaining = currentYearCap - currentYearUsed;
        
        // Total available = current year remaining + unused carry-forward
        const totalAvailable = currentYearRemaining + totalUnused;
        
        // Maximum contribution this year
        const maxContributionThisYear = currentYearCap + totalUnused;

        // Calculate potential tax savings
        const marginalTaxRate = 0.47; // Top marginal rate + Medicare levy
        const superTaxRate = FinancialConfig.SUPER_TAX_RATES.CONTRIBUTIONS_TAX; // 15%
        const taxSavingRate = marginalTaxRate - superTaxRate; // 32%
        const potentialTaxSaving = totalAvailable * taxSavingRate;

        // Calculate strategy for these circumstances
        const strategy = calculateOptimalStrategy(
            totalAvailable,
            currentYearCap,
            unusedAmounts,
            carryForwardData.totalSuperBalance
        );

        setResults({
            eligible: true,
            tsbThreshold,
            currentTSB: carryForwardData.totalSuperBalance,
            currentYear: {
                year: currentFY,
                cap: currentYearCap,
                used: currentYearUsed,
                remaining: currentYearRemaining
            },
            carryForward: {
                unusedAmounts,
                totalUnused,
                totalAvailable,
                maxContributionThisYear
            },
            taxSavings: {
                potentialSaving: potentialTaxSaving,
                marginalRate: marginalTaxRate,
                superRate: superTaxRate,
                savingRate: taxSavingRate
            },
            strategy
        });
        setActiveTab('results');
    };

    const calculateOptimalStrategy = (totalAvailable, currentCap, unusedAmounts, tsb) => {
        const strategies = [];

        // Strategy 1: Maximize current year
        if (totalAvailable > 0) {
            strategies.push({
                name: 'Maximize Current Year',
                description: `Contribute up to $${Math.min(totalAvailable, currentCap + unusedAmounts.reduce((s, y) => s + y.unused, 0)).toLocaleString()} this financial year`,
                amount: Math.min(totalAvailable, currentCap + unusedAmounts.reduce((s, y) => s + y.unused, 0)),
                benefit: 'Maximum tax savings in current year',
                considerations: [
                    'Requires sufficient cash flow',
                    'Uses oldest unused caps first (they expire soonest)',
                    'Best for high income earners'
                ]
            });
        }

        // Strategy 2: Spread over multiple years
        if (totalAvailable > currentCap) {
            const yearsNeeded = Math.ceil(totalAvailable / currentCap);
            strategies.push({
                name: 'Spread Contributions',
                description: `Contribute $${currentCap.toLocaleString()} per year over ${yearsNeeded} years`,
                amount: currentCap,
                benefit: 'More manageable cash flow, lower annual tax impact',
                considerations: [
                    `Will take ${yearsNeeded} years to use all available caps`,
                    'Risk of unused caps expiring (5-year limit)',
                    'Good for variable income'
                ]
            });
        }

        // Strategy 3: Priority on expiring caps
        const expiringNext = unusedAmounts.filter(y => (currentFY - y.year) === 4);
        if (expiringNext.length > 0) {
            const expiringAmount = expiringNext.reduce((s, y) => s + y.unused, 0);
            strategies.push({
                name: 'Use Expiring Caps First',
                description: `Prioritize $${expiringAmount.toLocaleString()} that expires next year`,
                amount: expiringAmount,
                benefit: 'Prevents loss of unused caps',
                considerations: [
                    'Caps from FY' + expiringNext[0].year + ' will expire after FY' + (currentFY + 1),
                    'Use it or lose it',
                    'Can combine with current year cap'
                ],
                urgent: true
            });
        }

        return strategies;
    };

    useEffect(() => {
        if (carryForwardData) {
            calculateCarryForward();
        }
    }, [carryForwardData]);

    const updateYear = (yearIndex, field, value) => {
        const updatedYears = [...carryForwardData.previousYears];
        updatedYears[yearIndex] = {
            ...updatedYears[yearIndex],
            [field]: Number(value)
        };
        setCarryForwardData({
            ...carryForwardData,
            previousYears: updatedYears
        });
    };

    const updateCurrentYear = (field, value) => {
        setCarryForwardData({
            ...carryForwardData,
            currentYear: {
                ...carryForwardData.currentYear,
                [field]: Number(value)
            }
        });
    };

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* TSB Check */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-semibold text-blue-900 mb-2">📊 Eligibility Check</h3>
                <p className="text-sm text-blue-800 mb-3">
                    Total Super Balance (TSB) must be less than ${FinancialConfig.TSB_THRESHOLDS.CARRY_FORWARD_CUTOFF.toLocaleString()} at start of financial year
                </p>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Super Balance (as at 30 June {currentFY})
                    </label>
                    <input
                        {...getNumericInputProps()}
                        value={carryForwardData.totalSuperBalance}
                        onChange={(e) => setCarryForwardData({
                            ...carryForwardData,
                            totalSuperBalance: Number(e.target.value)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
            </div>

            {/* Current Year */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Current Financial Year ({currentFY}-{currentFY + 1})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Concessional Cap
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={carryForwardData.currentYear.concessionalCap}
                            onChange={(e) => updateCurrentYear('concessionalCap', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            readOnly
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contributions Made This Year
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={carryForwardData.currentYear.concessionalUsed}
                            onChange={(e) => updateCurrentYear('concessionalUsed', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Previous Years */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Previous 4 Years (Carry-Forward Window)</h3>
                <div className="space-y-4">
                    {carryForwardData.previousYears.map((year, index) => (
                        <div key={year.year} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium mb-3">FY {year.year}-{year.year + 1}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Concessional Cap
                                    </label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={year.concessionalCap}
                                        onChange={(e) => updateYear(index, 'concessionalCap', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contributions Made
                                    </label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={year.concessionalUsed}
                                        onChange={(e) => updateYear(index, 'concessionalUsed', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Unused Amount
                                    </label>
                                    <input
                                        type="text"
                                        value={`$${Math.max(0, year.concessionalCap - year.concessionalUsed).toLocaleString()}`}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <button
                type="button"
                onClick={calculateCarryForward}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
            >
                Calculate Carry-Forward Capacity
            </button>
        </div>
    );

    const renderResults = () => {
        if (!results) return null;

        if (!results.eligible) {
            return (
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                    <h3 className="font-semibold text-red-900 text-lg mb-2">❌ Not Eligible for Carry-Forward</h3>
                    <p className="text-red-800">{results.reason}</p>
                    <p className="text-sm text-red-700 mt-4">
                        To be eligible for carry-forward contributions, your Total Super Balance must be below ${results.tsbThreshold.toLocaleString()} at the start of the financial year.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Eligibility Status */}
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
                    <h3 className="font-semibold text-green-900 text-lg mb-2">✅ Eligible for Carry-Forward</h3>
                    <p className="text-green-800">
                        TSB: ${results.currentTSB.toLocaleString()} (below ${results.tsbThreshold.toLocaleString()} threshold)
                    </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                        <div className="text-sm text-gray-600 mb-1">Current Year Remaining</div>
                        <div className="text-2xl font-bold text-blue-600">
                            ${results.currentYear.remaining.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                            of ${results.currentYear.cap.toLocaleString()} cap
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                        <div className="text-sm text-gray-600 mb-1">Unused from Previous Years</div>
                        <div className="text-2xl font-bold text-purple-600">
                            ${results.carryForward.totalUnused.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                            Available to carry forward
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                        <div className="text-sm text-gray-600 mb-1">Total Available</div>
                        <div className="text-2xl font-bold text-green-600">
                            ${results.carryForward.totalAvailable.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                            Max: ${results.carryForward.maxContributionThisYear.toLocaleString()} this year
                        </div>
                    </div>
                </div>

                {/* Tax Savings */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">💰 Potential Tax Savings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <div className="text-3xl font-bold text-green-600 mb-2">
                                ${results.taxSavings.potentialSaving.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                            <div className="text-sm text-gray-700">
                                If you use all ${results.carryForward.totalAvailable.toLocaleString()} available capacity
                            </div>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Marginal Tax Rate:</span>
                                <span className="font-semibold">{(results.taxSavings.marginalRate * 100).toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Super Contributions Tax:</span>
                                <span className="font-semibold">{(results.taxSavings.superRate * 100).toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                                <span>Net Tax Saving:</span>
                                <span className="font-semibold text-green-600">{(results.taxSavings.savingRate * 100).toFixed(1)}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Unused Amounts Breakdown */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">📅 Carry-Forward Details</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2 px-4">Financial Year</th>
                                    <th className="text-right py-2 px-4">Cap</th>
                                    <th className="text-right py-2 px-4">Used</th>
                                    <th className="text-right py-2 px-4">Unused</th>
                                    <th className="text-center py-2 px-4">Expires</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.carryForward.unusedAmounts.map((year) => {
                                    const yearsUntilExpiry = 5 - (currentFY - year.year);
                                    return (
                                        <tr key={year.year} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-4 font-medium">
                                                FY {year.year}-{year.year + 1}
                                            </td>
                                            <td className="text-right py-3 px-4">
                                                ${year.cap.toLocaleString()}
                                            </td>
                                            <td className="text-right py-3 px-4">
                                                ${year.used.toLocaleString()}
                                            </td>
                                            <td className="text-right py-3 px-4 font-semibold text-purple-600">
                                                ${year.unused.toLocaleString()}
                                            </td>
                                            <td className="text-center py-3 px-4">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    yearsUntilExpiry <= 1 ? 'bg-red-100 text-red-800' :
                                                    yearsUntilExpiry <= 2 ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-green-100 text-green-800'
                                                }`}>
                                                    {yearsUntilExpiry} {yearsUntilExpiry === 1 ? 'year' : 'years'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-50 font-semibold">
                                    <td colSpan="3" className="py-3 px-4 text-right">Total Unused:</td>
                                    <td className="text-right py-3 px-4 text-purple-600">
                                        ${results.carryForward.totalUnused.toLocaleString()}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Strategy Recommendations */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">🎯 Recommended Strategies</h3>
                    <div className="space-y-4">
                        {results.strategy.map((strategy, index) => (
                            <div 
                                key={index} 
                                className={`border rounded-lg p-4 ${
                                    strategy.urgent ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-semibold text-gray-900">
                                        {strategy.urgent && '⚠️ '}{strategy.name}
                                    </h4>
                                    <span className="text-lg font-bold text-blue-600">
                                        ${strategy.amount.toLocaleString()}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-700 mb-3">{strategy.description}</p>
                                <div className="bg-blue-50 p-3 rounded mb-3">
                                    <div className="text-sm font-medium text-blue-900">✓ {strategy.benefit}</div>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <div className="font-medium mb-1">Considerations:</div>
                                    <ul className="list-disc list-inside space-y-1">
                                        {strategy.considerations.map((consideration, i) => (
                                            <li key={i}>{consideration}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <CalculationTransparency
                    kind="smsf"
                    title="Carry-Forward Transparency"
                    inputs={{
                        totalBalance: carryForwardData.totalSuperBalance,
                        contributionAmount: carryForwardData.plannedContribution,
                    }}
                    results={{
                        ...results,
                        tableData: results.carryForward?.unusedAmounts || [],
                        recommendations: results.strategy || [],
                    }}
                />
            </div>
        );
    };

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 Understanding Carry-Forward Rules</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">What is Contribution Carry-Forward?</h4>
                        <p className="text-gray-700">
                            Since 1 July 2018, you can carry forward unused concessional contribution cap amounts for up to 5 years. 
                            This allows you to make catch-up contributions if you didn't maximize your contributions in previous years.
                        </p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Eligibility Requirements</h4>
                        <ul className="list-disc list-inside space-y-2 text-blue-800">
                            <li>Total Super Balance must be less than ${FinancialConfig.TSB_THRESHOLDS.CARRY_FORWARD_CUTOFF.toLocaleString()} at 30 June of previous financial year</li>
                            <li>Can only carry forward unused concessional cap amounts</li>
                            <li>Cannot carry forward non-concessional cap amounts</li>
                            <li>Unused amounts expire after 5 years</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">How It Works</h4>
                        <div className="space-y-2 text-gray-700">
                            <p><strong>Step 1:</strong> Check your TSB at 30 June of the previous financial year</p>
                            <p><strong>Step 2:</strong> If below ${FinancialConfig.TSB_THRESHOLDS.CARRY_FORWARD_CUTOFF.toLocaleString()}, calculate unused caps from previous 5 years</p>
                            <p><strong>Step 3:</strong> Current year cap + unused carry-forward = maximum you can contribute</p>
                            <p><strong>Step 4:</strong> Oldest unused amounts are used first</p>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Example Scenario</h4>
                        <div className="text-green-800 space-y-2">
                            <p>Jane has TSB of $800,000 and these unused caps:</p>
                            <ul className="list-disc list-inside ml-4">
                                <li>2020-21: $5,000 unused</li>
                                <li>2021-22: $10,000 unused</li>
                                <li>2022-23: $15,000 unused</li>
                                <li>2023-24: $20,000 unused</li>
                            </ul>
                            <p className="font-semibold mt-2">
                                In 2024-25, Jane can contribute up to $80,000:
                            </p>
                            <p className="ml-4">
                                Current cap (${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()}) + Carry-forward ($50,000) = ${(FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL + 50000).toLocaleString()}
                            </p>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Benefits</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li>Catch up on missed contributions from previous years</li>
                            <li>Maximize tax-effective super contributions</li>
                            <li>Particularly valuable after low-income years or career breaks</li>
                            <li>Can significantly reduce tax for high earners</li>
                            <li>Boost retirement savings efficiently</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Considerations</h4>
                        <ul className="list-disc list-inside space-y-1 text-yellow-800">
                            <li>Unused amounts expire after 5 years (use it or lose it)</li>
                            <li>TSB test applies each year</li>
                            <li>Need sufficient cash flow to make large contributions</li>
                            <li>Ensure SMSF can accept large contributions (liquidity)</li>
                            <li>Consider Division 293 tax if income exceeds $250,000</li>
                            <li>Seek financial advice for complex situations</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Contribution Carry-Forward Calculator</h2>
                <p className="text-gray-600">
                    Track and maximize your unused concessional contribution caps over 5 years
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'calculator', label: 'Calculator', icon: '🧮' },
                        { id: 'results', label: 'Results', icon: '📊' },
                        { id: 'education', label: 'How It Works', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'calculator' && renderCalculator()}
                {activeTab === 'results' && renderResults()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFContributionCarryForward.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFContributionCarryForward;

